/**
 * src/lib/capture/captureAndFile.js — v24.0 Knowledge Vault, Phase 102 slice E.
 *
 * The WikiLLM "entry endpoint" from the user's point of view: drop any artifact →
 * it lands where it belongs. This is a PURE decision flow — it owns NO transport,
 * NO new server route; it sequences the EXISTING capture actions:
 *   sha256 + MIME-validate → classify → ALWAYS one-screen scope-confirm →
 *   store-by-size → file into the existing subsystem (register | add_note |
 *   presign→register | invoice_intake).
 *
 * Every side effect is INJECTED (deps) so the routing + invariants are unit-tested
 * without a network call (tests/lib/capture/captureAndFile.test.js). The real deps
 * are wired by the UI (buildCaptureDeps) from the api client + Supabase upload +
 * the ScopeConfirmSheet.
 *
 * Invariants (the hard rules): validate-before-storage (AC7), idempotent on
 * content_sha256 (register dedups → deduped:true, AC5), invoices NEVER hit
 * project_documents (AC2), never orphaned (the confirm screen always resolves a
 * scope), never a new silo.
 */

import { isCadFileName, CAD_MIME } from '../../../lib/files/cadFiles.mjs';

// Mirror of the server register allowlist (lib/handlers/documents-registry.js
// ALLOWED_MIME) — the client rejects early for fast feedback; the server
// re-validates (source of truth). Keep in sync if either changes.
export const ALLOWED_CAPTURE_MIME = new Set([
  'application/pdf',
  'image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif',
  'text/plain', 'text/csv', 'text/markdown',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);

const SUPABASE_MAX_BYTES = 25 * 1024 * 1024;

/** TICKET-908: may this file be captured? Allowed MIME, or a CAD/3D extension. */
export function isCapturableFile(file) {
  return ALLOWED_CAPTURE_MIME.has(file?.type) || isCadFileName(file?.name);
}

function normalizeScope(scope) {
  const s = scope && typeof scope === 'object' ? scope : {};
  return { project_id: s.project_id || null, deal_id: s.deal_id || null, vessel_id: s.vessel_id || null };
}

function hasAnchor(scope) {
  return !!(scope.project_id || scope.deal_id || scope.vessel_id);
}

// Route DERIVED from the CONFIRMED type + size (the user may have corrected the
// type on the sheet). Mirrors the server deriveRoute precedence: invoice > note >
// size(>25MB) > register. Kept local — client/server module boundary.
function deriveRoute(document_type, size, isCad = false) {
  if (document_type === 'invoice') return 'invoice_intake';
  if (document_type === 'note') return 'add_note';
  // TICKET-908: a CAD/3D file is never a gallery photo, whatever it was typed as.
  if (isCad) return Number(size) > SUPABASE_MAX_BYTES ? 'presign' : 'register';
  // A photo (<=25MB) goes into the PHOTO folder via the dedicated photo pipeline
  // (thumbnail/buffer/EXIF/eviction), NOT the generic documents register — so it
  // is a first-class managed gallery photo. Oversize photos still presign→register.
  if (document_type === 'photo' && Number(size) <= SUPABASE_MAX_BYTES) return 'photo';
  if (Number(size) > SUPABASE_MAX_BYTES) return 'presign';
  return 'register';
}

/**
 * @param {object} input — a File-like {name,type,size}, OR a typed note {kind:'note', text}
 * @param {object} context — current view scope { project_id, deal_id, vessel_id }
 * @param {object} deps — injected side effects (see below); all async
 * @returns {Promise<{filed:boolean, kind?:string, id?:string, deduped?:boolean, cancelled?:boolean}>}
 */
export async function captureAndFile(input, context = {}, deps = {}) {
  const {
    sha256, readPreview, classify, confirmScope,
    uploadSupabase, presign, putToUrl, registerDoc, photosRegister, addNote, invoiceIntake,
  } = deps;

  // ── Typed note (no file): straight to add_note, no classify/upload (spec step 6).
  if (input && input.kind === 'note') {
    const text = String(input.text || '').trim();
    if (!text) throw new Error('empty_note');
    const decision = await confirmScope({
      document_type: 'note', product_types: [], scope: normalizeScope(context),
      confidence: 1, needs_confirm: !hasAnchor(normalizeScope(context)), route: 'add_note',
    });
    if (!decision) return { filed: false, cancelled: true };
    const r = await addNote({ ...decision.scope, text, product_types: decision.product_types || [] });
    return { filed: true, kind: 'note', id: r.entry_id, deduped: !!r.deduped };
  }

  // ── File path. AC7: validate MIME BEFORE any sha/classify/upload.
  const file = input;
  // TICKET-908: CAD/3D files pass by extension and travel as opaque binary.
  const isCad = isCadFileName(file?.name);
  const mime = isCad ? CAD_MIME : (file?.type || null);
  if (mime && !isCad && !ALLOWED_CAPTURE_MIME.has(mime)) {
    throw new Error('unsupported_mime');
  }

  const content_sha256 = await sha256(file);
  const content_preview = await readPreview(file);

  const classified = await classify({
    file_name: file.name,
    mime_type: mime,
    file_size_bytes: file.size,
    content_preview,
    context: normalizeScope(context),
  });

  // TICKET-1116 (CEO 2026-10-07): uploading from inside a project/deal files there
  // without the sheet — the open record answers "where does this file?". The view
  // context wins over the classifier's suggestion. The pre-filled, editable sheet
  // still shows for a context-less upload or when the classifier is unsure.
  const ctx = normalizeScope(context);
  const autoFile = hasAnchor(ctx) && !classified.needs_confirm;
  const suggested = normalizeScope(classified.suggested_scope);
  const decision = autoFile ? {
    scope: {
      project_id: ctx.project_id || (ctx.deal_id ? null : suggested.project_id),
      deal_id: ctx.deal_id || suggested.deal_id,
      vessel_id: ctx.vessel_id || suggested.vessel_id,
    },
    document_type: classified.document_type,
    product_types: classified.product_types || [],
  } : await confirmScope({
    document_type: classified.document_type,
    product_types: classified.product_types || [],
    scope: classified.suggested_scope,
    confidence: classified.confidence,
    needs_confirm: classified.needs_confirm,
    route: classified.route,
  });
  if (!decision) return { filed: false, cancelled: true };

  const scope = normalizeScope(decision.scope);
  const document_type = decision.document_type;
  const product_types = decision.product_types || [];
  const route = deriveRoute(document_type, file.size, isCad);

  // Auto-fill: the model's lifted metadata (addressee/vessel/title/summary) rides
  // along to the registry row so the Files tab shows "for Roger · Hanse 400E"
  // without re-opening the file. Carried only when something was actually inferred.
  const extracted = classified.extracted && Object.values(classified.extracted).some(Boolean)
    ? classified.extracted : null;

  if (route === 'invoice_intake') {
    const r = await invoiceIntake(file, scope);
    return { filed: true, kind: 'invoice', id: r.id, deduped: !!r.deduped };
  }

  if (route === 'add_note') {
    // A dropped text file the model read as a note → file its text as a brain chip.
    const text = (content_preview || '').trim() || file.name;
    const r = await addNote({ ...scope, text, product_types });
    return { filed: true, kind: 'note', id: r.entry_id, deduped: !!r.deduped };
  }

  if (route === 'photo') {
    // Image -> photo-buffer (uploadSupabase buckets images there) -> photos.register
    // so it gets the full photo pipeline + lands in the project's photo gallery.
    const up = await uploadSupabase(file, { document_type, content_sha256 });
    const r = await photosRegister({
      ...scope, content_sha256, product_types,
      file_name: file.name, mime_type: mime, file_size: file.size,
      buffer_path: up.file_path,
      // TICKET-472 — the public 320px thumb, so the gallery tile renders without
      // presigning and downloading the full-resolution original. Absent only when
      // the thumbnail could not be produced (undecodable image / no canvas).
      thumb_path: up.thumb_path,
    });
    return { filed: true, kind: 'photo', id: r.document_id, deduped: !!r.deduped };
  }

  if (route === 'presign') {
    const ps = await presign({ ...scope, file_name: file.name, file_size_bytes: file.size });
    await putToUrl(ps.upload_url, file);
    const r = await registerDoc({
      ...scope, document_type, content_sha256, product_types,
      file_name: file.name, mime_type: mime, file_size: file.size,
      // TICKET-1329: the canonical scheme every reader branches on. The bare key was
      // read as a Supabase path, so a 42MB capture PDF never opened.
      file_path: `hetzner:${ps.bucket}/${ps.object_key}`,
      document_metadata: { storage_backend: ps.storage_backend, bucket: ps.bucket, ...(extracted ? { extracted } : {}) },
      ...(extracted?.summary ? { summary: extracted.summary } : {}),
    });
    return { filed: true, kind: 'document', id: r.document_id, deduped: !!r.deduped };
  }

  // register (<=25MB) → upload to Supabase then file the registry row.
  const up = await uploadSupabase(file, { document_type, content_sha256 });
  const r = await registerDoc({
    ...scope, document_type, content_sha256, product_types,
    file_name: file.name, mime_type: mime, file_size: file.size,
    file_path: up.file_path,
    ...(extracted ? { document_metadata: { extracted }, summary: extracted.summary || null } : {}),
  });
  return { filed: true, kind: 'document', id: r.document_id, deduped: !!r.deduped };
}
